import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Calendar, Clock, Music, Map, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '@/components/landing/Header';
import { db } from '@/lib/firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { config } from '@/lib/config';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { isGigExpired, formatGigTime12h } from '@/lib/gigUtils';

const FindPerformerPage = () => {
  const mobile = useMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDay, setSelectedDay] = useState('all');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('28');
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredGigs, setFilteredGigs] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowsRef = useRef([]);
  
  // Pagination settings
  const resultsPerPage = mobile.isMobile ? 5 : 9;
  const totalPages = Math.ceil(filteredGigs.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const paginatedGigs = filteredGigs.slice(startIndex, endIndex);

  // Color mapping for days (similar to SpeedQuizzing)
  const dayColors = {
    'monday': '#EF4444',    // Red
    'tuesday': '#A855F7',   // Purple
    'wednesday': '#10B981', // Green
    'thursday': '#F97316',  // Orange
    'friday': '#F97316',    // Orange
    'saturday': '#EC4899',  // Pink
    'sunday': '#A855F7',   // Purple
    'all': '#EAB308'        // Yellow (default)
  };

  // Test data - will be replaced with real data from Firestore
  const testGigs = [
    {
      id: '1',
      venue_name: 'The Red Lion',
      date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:00',
      address: '123 High Street',
      postcode: 'SW1A 1AA',
      city: 'London',
      lat: 51.5074,
      lon: -0.1278,
      user_id: 'artist1',
      artist_name: 'John Smith',
      artist_url_slug: 'john-smith',
      artist_genre: 'Acoustic Folk'
    },
    {
      id: '2',
      venue_name: 'The Blue Moon',
      date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:30',
      address: '456 Market Square',
      postcode: 'M1 1AA',
      city: 'Manchester',
      lat: 53.4808,
      lon: -2.2426,
      user_id: 'artist2',
      artist_name: 'Sarah Johnson',
      artist_url_slug: 'sarah-johnson',
      artist_genre: 'Country Rock'
    },
    {
      id: '3',
      venue_name: 'The Green Dragon',
      date: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:00',
      address: '789 Park Lane',
      postcode: 'B1 1AA',
      city: 'Birmingham',
      lat: 52.4862,
      lon: -1.8904,
      user_id: 'artist3',
      artist_name: 'Mike Wilson',
      artist_url_slug: 'mike-wilson',
      artist_genre: 'Blues'
    },
    {
      id: '4',
      venue_name: 'The Golden Fleece',
      date: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:30',
      address: '321 Queen Street',
      postcode: 'LS1 1AA',
      city: 'Leeds',
      lat: 53.8008,
      lon: -1.5491,
      user_id: 'artist4',
      artist_name: 'Emma Davis',
      artist_url_slug: 'emma-davis',
      artist_genre: 'Pop'
    },
    {
      id: '5',
      venue_name: 'The Crown & Anchor',
      date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:00',
      address: '654 Main Road',
      postcode: 'L1 1AA',
      city: 'Liverpool',
      lat: 53.4084,
      lon: -2.9916,
      user_id: 'artist5',
      artist_name: 'Tom Brown',
      artist_url_slug: 'tom-brown',
      artist_genre: 'Rock'
    },
    {
      id: '6',
      venue_name: 'The White Horse',
      date: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:00',
      address: '987 Church Street',
      postcode: 'N1 1AA',
      city: 'Newcastle',
      lat: 54.9783,
      lon: -1.6178,
      user_id: 'artist6',
      artist_name: 'Lisa Anderson',
      artist_url_slug: 'lisa-anderson',
      artist_genre: 'Jazz'
    },
    {
      id: '7',
      venue_name: 'The Rose & Crown',
      date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:15',
      address: '45 King Street',
      postcode: 'S1 2HD',
      city: 'Sheffield',
      lat: 53.3811,
      lon: -1.4701,
      user_id: 'artist7',
      artist_name: 'David Taylor',
      artist_url_slug: 'david-taylor',
      artist_genre: 'Indie Rock'
    },
    {
      id: '8',
      venue_name: 'The Old Crown',
      date: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:45',
      address: '78 Bridge Street',
      postcode: 'NG1 5BD',
      city: 'Nottingham',
      lat: 52.9548,
      lon: -1.1581,
      user_id: 'artist8',
      artist_name: 'Rachel Green',
      artist_url_slug: 'rachel-green',
      artist_genre: 'Soul'
    },
    {
      id: '9',
      venue_name: 'The King\'s Head',
      date: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:30',
      address: '12 Castle Street',
      postcode: 'CF10 1SZ',
      city: 'Cardiff',
      lat: 51.4816,
      lon: -3.1791,
      user_id: 'artist9',
      artist_name: 'James Parker',
      artist_url_slug: 'james-parker',
      artist_genre: 'Alternative'
    },
    {
      id: '10',
      venue_name: 'The Royal Oak',
      date: new Date(Date.now() + 11 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:00',
      address: '234 Princes Street',
      postcode: 'EH2 4BA',
      city: 'Edinburgh',
      lat: 55.9533,
      lon: -3.1883,
      user_id: 'artist10',
      artist_name: 'Sophie Miller',
      artist_url_slug: 'sophie-miller',
      artist_genre: 'Folk'
    },
    {
      id: '11',
      venue_name: 'The Bell Inn',
      date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:00',
      address: '56 Guildhall Street',
      postcode: 'PE29 3HX',
      city: 'Cambridge',
      lat: 52.2053,
      lon: 0.1218,
      user_id: 'artist11',
      artist_name: 'Oliver White',
      artist_url_slug: 'oliver-white',
      artist_genre: 'Singer-Songwriter'
    },
    {
      id: '12',
      venue_name: 'The George & Dragon',
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:45',
      address: '89 High Street',
      postcode: 'OX1 4AS',
      city: 'Oxford',
      lat: 51.7520,
      lon: -1.2577,
      user_id: 'artist12',
      artist_name: 'Amelia Jones',
      artist_url_slug: 'amelia-jones',
      artist_genre: 'Pop Rock'
    },
    {
      id: '13',
      venue_name: 'The Ship Inn',
      date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:30',
      address: '123 Marine Parade',
      postcode: 'BN2 1TE',
      city: 'Brighton',
      lat: 50.8225,
      lon: -0.1372,
      user_id: 'artist13',
      artist_name: 'Charlie Harris',
      artist_url_slug: 'charlie-harris',
      artist_genre: 'Rock'
    },
    {
      id: '14',
      venue_name: 'The Coach & Horses',
      date: new Date(Date.now() + 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:15',
      address: '67 Victoria Road',
      postcode: 'BA1 3PD',
      city: 'Bath',
      lat: 51.3758,
      lon: -2.3599,
      user_id: 'artist14',
      artist_name: 'Grace Lee',
      artist_url_slug: 'grace-lee',
      artist_genre: 'Acoustic'
    },
    {
      id: '15',
      venue_name: 'The Three Tuns',
      date: new Date(Date.now() + 16 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:00',
      address: '34 Fore Street',
      postcode: 'EX4 3AT',
      city: 'Exeter',
      lat: 50.7184,
      lon: -3.5339,
      user_id: 'artist15',
      artist_name: 'Nathan Clark',
      artist_url_slug: 'nathan-clark',
      artist_genre: 'Indie'
    },
    {
      id: '16',
      venue_name: 'The Plough',
      date: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:45',
      address: '12 Market Place',
      postcode: 'YO1 7HH',
      city: 'York',
      lat: 53.9600,
      lon: -1.0873,
      user_id: 'artist16',
      artist_name: 'Isabella Wright',
      artist_url_slug: 'isabella-wright',
      artist_genre: 'Country'
    },
    {
      id: '17',
      venue_name: 'The Fleece',
      date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:30',
      address: '78 St Peter\'s Street',
      postcode: 'AL1 3EG',
      city: 'St Albans',
      lat: 51.7537,
      lon: -0.3363,
      user_id: 'artist17',
      artist_name: 'Lucas Martinez',
      artist_url_slug: 'lucas-martinez',
      artist_genre: 'Latin Pop'
    },
    {
      id: '18',
      venue_name: 'The Star & Garter',
      date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:00',
      address: '45 Castle Street',
      postcode: 'CT1 2TN',
      city: 'Canterbury',
      lat: 51.2802,
      lon: 1.0789,
      user_id: 'artist18',
      artist_name: 'Hannah Thompson',
      artist_url_slug: 'hannah-thompson',
      artist_genre: 'Pop'
    },
    {
      id: '19',
      venue_name: 'The Anchor',
      date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:00',
      address: '23 Quay Street',
      postcode: 'IP4 1BY',
      city: 'Ipswich',
      lat: 52.0567,
      lon: 1.1482,
      user_id: 'artist19',
      artist_name: 'Ethan Walker',
      artist_url_slug: 'ethan-walker',
      artist_genre: 'Rock'
    },
    {
      id: '20',
      venue_name: 'The Railway Tavern',
      date: new Date(Date.now() + 17 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:45',
      address: '56 Station Road',
      postcode: 'RG1 1LT',
      city: 'Reading',
      lat: 51.4543,
      lon: -0.9781,
      user_id: 'artist20',
      artist_name: 'Mia Roberts',
      artist_url_slug: 'mia-roberts',
      artist_genre: 'R&B'
    },
    {
      id: '21',
      venue_name: 'The Black Swan',
      date: new Date(Date.now() + 19 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:15',
      address: '89 Newgate Street',
      postcode: 'DL1 5RG',
      city: 'Darlington',
      lat: 54.5277,
      lon: -1.5536,
      user_id: 'artist21',
      artist_name: 'Daniel King',
      artist_url_slug: 'daniel-king',
      artist_genre: 'Blues Rock'
    },
    {
      id: '22',
      venue_name: 'The Swan Hotel',
      date: new Date(Date.now() + 22 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:30',
      address: '12 The Butts',
      postcode: 'CV34 4SS',
      city: 'Warwick',
      lat: 52.2823,
      lon: -1.5924,
      user_id: 'artist22',
      artist_name: 'Emily Chen',
      artist_url_slug: 'emily-chen',
      artist_genre: 'Pop'
    },
    {
      id: '23',
      venue_name: 'The Red Cow',
      date: new Date(Date.now() + 24 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:00',
      address: '34 Chapel Street',
      postcode: 'LU1 2SE',
      city: 'Luton',
      lat: 51.8797,
      lon: -0.4175,
      user_id: 'artist23',
      artist_name: 'Ryan Mitchell',
      artist_url_slug: 'ryan-mitchell',
      artist_genre: 'Indie Rock'
    },
    {
      id: '24',
      venue_name: 'The Crown',
      date: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:30',
      address: '67 High Street',
      postcode: 'DE1 3GF',
      city: 'Derby',
      lat: 52.9225,
      lon: -1.4746,
      user_id: 'artist24',
      artist_name: 'Olivia Scott',
      artist_url_slug: 'olivia-scott',
      artist_genre: 'Folk Pop'
    },
    {
      id: '25',
      venue_name: 'The Greyhound',
      date: new Date(Date.now() + 26 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:45',
      address: '23 Church Lane',
      postcode: 'PO1 1BX',
      city: 'Portsmouth',
      lat: 50.8198,
      lon: -1.0880,
      user_id: 'artist25',
      artist_name: 'Noah Adams',
      artist_url_slug: 'noah-adams',
      artist_genre: 'Acoustic Rock'
    },
    {
      id: '26',
      venue_name: 'The White Hart',
      date: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:00',
      address: '45 Market Place',
      postcode: 'GL50 1HE',
      city: 'Cheltenham',
      lat: 51.8994,
      lon: -2.0783,
      user_id: 'artist26',
      artist_name: 'Ava Garcia',
      artist_url_slug: 'ava-garcia',
      artist_genre: 'Soul Pop'
    },
    {
      id: '27',
      venue_name: 'The Bull',
      date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:15',
      address: '78 London Road',
      postcode: 'SO14 2DJ',
      city: 'Southampton',
      lat: 50.9097,
      lon: -1.4044,
      user_id: 'artist27',
      artist_name: 'Jack Robinson',
      artist_url_slug: 'jack-robinson',
      artist_genre: 'Rock'
    },
    {
      id: '28',
      venue_name: 'The Victoria',
      date: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '19:00',
      address: '12 King Street',
      postcode: 'NG31 6QW',
      city: 'Grantham',
      lat: 52.9115,
      lon: -0.6418,
      user_id: 'artist28',
      artist_name: 'Lily Baker',
      artist_url_slug: 'lily-baker',
      artist_genre: 'Pop'
    },
    {
      id: '29',
      venue_name: 'The Talbot',
      date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:45',
      address: '34 Main Street',
      postcode: 'DN15 6DS',
      city: 'Scunthorpe',
      lat: 53.5897,
      lon: -0.6504,
      user_id: 'artist29',
      artist_name: 'Ben Cooper',
      artist_url_slug: 'ben-cooper',
      artist_genre: 'Blues'
    },
    {
      id: '30',
      venue_name: 'The King\'s Arms',
      date: new Date(Date.now() + 31 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:30',
      address: '56 Bridge Road',
      postcode: 'BN3 3AF',
      city: 'Hove',
      lat: 50.8279,
      lon: -0.1687,
      user_id: 'artist30',
      artist_name: 'Zoe Taylor',
      artist_url_slug: 'zoe-taylor',
      artist_genre: 'Jazz Pop'
    },
    {
      id: '31',
      venue_name: 'The Prince of Wales',
      date: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:00',
      address: '45 Union Street',
      postcode: 'AB11 5PH',
      city: 'Aberdeen',
      lat: 57.1497,
      lon: -2.0943,
      user_id: 'artist31',
      artist_name: 'Fraser MacLeod',
      artist_url_slug: 'fraser-macleod',
      artist_genre: 'Scottish Folk'
    },
    {
      id: '32',
      venue_name: 'The Crown Bar',
      date: new Date(Date.now() + 26 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:30',
      address: '46 Great Victoria Street',
      postcode: 'BT2 7BA',
      city: 'Belfast',
      lat: 54.5973,
      lon: -5.9301,
      user_id: 'artist32',
      artist_name: 'Connor O\'Neill',
      artist_url_slug: 'connor-oneill',
      artist_genre: 'Irish Rock'
    },
    {
      id: '33',
      venue_name: 'The Temple Bar',
      date: new Date(Date.now() + 32 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '21:00',
      address: '47-48 Temple Bar',
      postcode: 'D02 EK42',
      city: 'Dublin',
      lat: 53.3459,
      lon: -6.2634,
      user_id: 'artist33',
      artist_name: 'Siobhan Murphy',
      artist_url_slug: 'siobhan-murphy',
      artist_genre: 'Celtic Folk'
    },
    {
      id: '34',
      venue_name: 'The Oliver Plunkett',
      date: new Date(Date.now() + 33 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      time: '20:15',
      address: '116 Oliver Plunkett Street',
      postcode: 'T12 TR27',
      city: 'Cork',
      lat: 51.8985,
      lon: -8.4756,
      user_id: 'artist34',
      artist_name: 'Padraig Walsh',
      artist_url_slug: 'padraig-walsh',
      artist_genre: 'Traditional Irish'
    }
  ];

  // Load Google Maps API
  useEffect(() => {
    const apiKey = config.googleMapsApiKey;
    if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
      console.warn('Google Maps API key not configured');
      setMapLoaded(false);
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.Map) {
      setMapLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => setMapLoaded(true));
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.maps) {
        setMapLoaded(true);
      }
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      setMapLoaded(false);
    };
    document.head.appendChild(script);

    return () => {
      // Don't remove script on cleanup as it might be used by other components
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !window.google || !window.google.maps || !mapRef.current) return;

    // Ensure map container has dimensions
    if (mapRef.current.offsetWidth === 0 || mapRef.current.offsetHeight === 0) {
      console.warn('Map container has no dimensions, retrying...');
      setTimeout(() => {
        if (mapRef.current && mapRef.current.offsetWidth > 0 && mapRef.current.offsetHeight > 0) {
          initializeMap();
        }
      }, 100);
      return;
    }

    initializeMap();
    
    function initializeMap() {
      const google = window.google;
      const center = { lat: 54.7024, lng: -3.2766 }; // Center of UK

      // Only create map if it doesn't exist
      if (mapInstanceRef.current) {
        return;
      }

      try {
        const map = new google.maps.Map(mapRef.current, {
          zoom: 6,
          center: center,
          styles: [
            {
              featureType: 'all',
              elementType: 'geometry',
              stylers: [{ color: '#242f3e' }]
            },
            {
              featureType: 'all',
              elementType: 'labels.text.stroke',
              stylers: [{ color: '#242f3e' }]
            },
            {
              featureType: 'all',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#746855' }]
            },
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#17263c' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry',
              stylers: [{ color: '#38414e' }]
            }
          ]
        });

        mapInstanceRef.current = map;
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }
  }, [mapLoaded]);

  // Load gigs from Firestore
  useEffect(() => {
    const loadGigs = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const gigsQuery = query(
          collection(db, 'gigs'),
          orderBy('date', 'asc')
        );
        const querySnapshot = await getDocs(gigsQuery);
        
        const gigsData = [];
        // Get start of today in UTC
        const todayStart = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        todayStart.setUTCHours(0, 0, 0, 0);
        
        for (const docSnap of querySnapshot.docs) {
          const gigData = { id: docSnap.id, ...docSnap.data() };
          
          // Handle date format - could be YYYY-MM-DD string or Date object
          let gigDate;
          if (typeof gigData.date === 'string') {
            // If it's a string like "2025-11-21", parse it properly
            const dateParts = gigData.date.split('-');
            if (dateParts.length === 3) {
              gigDate = new Date(Date.UTC(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])));
            } else {
              gigDate = new Date(gigData.date);
            }
          } else {
            gigDate = new Date(gigData.date);
          }
          
          // Filter out expired gigs using local time (gigs expire after midnight on their date)
          // Show gig if it hasn't expired (using local time)
          // Remove lat/lon requirement - show all gigs, geocoding is optional
          if (!isGigExpired(gigData.date)) {
            try {
              const profileDoc = await getDoc(doc(db, 'profiles', gigData.user_id));
              if (profileDoc.exists()) {
                const profileData = profileDoc.data();
                gigData.artist_name = profileData.artist_name || profileData.karaoke_name || 'Unknown Artist';
                gigData.artist_url_slug = profileData.url_slug || gigData.user_id;
                gigData.artist_genre = profileData.genre || '';
              }
            } catch (error) {
              console.error('Error loading artist profile:', error);
            }
            
            gigsData.push(gigData);
          }
        }
        
        // Add 5 placeholder gigs scheduled after November 23rd, 2025
        const nov23Start = new Date(Date.UTC(2025, 10, 23)); // November 23, 2025 (month is 0-indexed)
        nov23Start.setUTCHours(0, 0, 0, 0);
        
        const placeholderGigs = testGigs
          .filter(gig => {
            const gigDate = new Date(gig.date);
            const gigDateStart = new Date(Date.UTC(gigDate.getUTCFullYear(), gigDate.getUTCMonth(), gigDate.getUTCDate()));
            gigDateStart.setUTCHours(0, 0, 0, 0);
            return gigDateStart >= nov23Start;
          })
          .slice(0, 5); // Take first 5
        
        // Merge real gigs with placeholder gigs
        const allGigs = [...gigsData, ...placeholderGigs];
        
        // Sort by date
        allGigs.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });
        
        setGigs(allGigs);
        console.log(`✅ Loaded ${gigsData.length} real gigs + ${placeholderGigs.length} placeholder gigs`);
      } catch (error) {
        console.error('Error loading gigs:', error);
        // Only show test gigs if there's an error AND no real gigs were loaded
        setGigs(testGigs);
      } finally {
        setLoading(false);
      }
    };

    loadGigs();
  }, []);

  // Filter gigs
  useEffect(() => {
    const now = new Date();
    // Get today's date at midnight (local time)
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    todayLocal.setHours(0, 0, 0, 0);
    
    let filtered = gigs.filter(gig => {
      // First, filter out expired gigs (gigs that have passed after midnight locally)
      if (isGigExpired(gig.date)) {
        return false;
      }
      
      // Handle date format - could be YYYY-MM-DD string or Date object
      let gigDate;
      if (typeof gig.date === 'string') {
        const dateParts = gig.date.split('-');
        if (dateParts.length === 3) {
          gigDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])); // Local date
        } else {
          gigDate = new Date(gig.date);
        }
      } else {
        gigDate = new Date(gig.date);
      }
      
      // Get start of gig date (local time)
      const gigDateStart = new Date(gigDate.getFullYear(), gigDate.getMonth(), gigDate.getDate());
      gigDateStart.setHours(0, 0, 0, 0);
      
      const gigDay = gigDate.getDay();
      
      // Calculate days difference using local date comparison
      const daysFromNow = Math.floor((gigDateStart - todayLocal) / (1000 * 60 * 60 * 24));
      let timeFrameMatch = false;
      
      if (selectedTimeFrame === '0') {
        timeFrameMatch = daysFromNow === 0;
      } else {
        timeFrameMatch = daysFromNow >= 0 && daysFromNow <= parseInt(selectedTimeFrame);
      }
      
      if (!timeFrameMatch) return false;
      
      if (selectedDay !== 'all') {
        const dayMap = { 'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4, 'friday': 5, 'saturday': 6, 'sunday': 0 };
        if (dayMap[selectedDay] !== gigDay) return false;
      }
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          gig.venue_name?.toLowerCase().includes(searchLower) ||
          gig.address?.toLowerCase().includes(searchLower) ||
          gig.postcode?.toLowerCase().includes(searchLower) ||
          gig.city?.toLowerCase().includes(searchLower) ||
          gig.artist_name?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }
      
      return true;
    });
    
    filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
    setFilteredGigs(filtered);
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [gigs, searchTerm, selectedDay, selectedTimeFrame]);

  // Update map markers when filtered gigs change
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google) return;

    const google = window.google;
    const map = mapInstanceRef.current;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    infoWindowsRef.current.forEach(window => window.close());
    markersRef.current = [];
    infoWindowsRef.current = [];

    if (filteredGigs.length === 0) return;

    // Create markers for filtered gigs
    const bounds = new google.maps.LatLngBounds();
    
    filteredGigs.forEach(gig => {
      if (!gig.lat || !gig.lon) return;

      const gigDate = new Date(gig.date);
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[gigDate.getDay()];
      const color = selectedDay === 'all' ? dayColors[dayName] : dayColors[selectedDay] || dayColors.all;

      // Create custom marker icon
      const markerIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2
      };

      const position = { lat: gig.lat, lng: gig.lon };
      const marker = new google.maps.Marker({
        position,
        map,
        icon: markerIcon,
        title: gig.venue_name
      });

      const dateInfo = formatDate(gig.date);
      const timeStr = formatGigTime12h(gig.time);
      const mapUrl = getMapUrl(gig);
      
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="color: #000; min-width: 200px;">
            <h3 style="font-weight: bold; margin-bottom: 8px; font-size: 16px;">${gig.venue_name || gig.venue || 'Venue TBC'}</h3>
            <p style="margin: 4px 0; font-size: 14px; color: #666;">
              <strong>Date:</strong> ${dateInfo.dayName}, ${dateInfo.day} ${dateInfo.month} ${dateInfo.year}<br>
              ${timeStr ? `<strong>Time:</strong> ${timeStr}<br>` : ''}
              ${gig.address ? `<strong>Address:</strong> ${gig.address}<br>` : ''}
              ${gig.postcode ? `<strong>Postcode:</strong> ${gig.postcode}<br>` : ''}
              ${gig.city ? `<strong>City:</strong> ${gig.city}<br>` : ''}
              ${gig.artist_name ? `<strong>Artist:</strong> ${gig.artist_name}<br>` : ''}
            </p>
            <div style="display: flex; gap: 8px; margin-top: 8px;">
              ${mapUrl ? `<a href="${mapUrl}" target="_blank" style="color: #2563eb; text-decoration: underline; font-size: 13px;">📍 Directions</a>` : ''}
              ${gig.artist_url_slug ? `<a href="/${gig.artist_url_slug}" style="color: #7c3aed; text-decoration: underline; font-size: 13px;">View Profile</a>` : ''}
            </div>
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindowsRef.current.forEach(w => w.close());
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
      infoWindowsRef.current.push(infoWindow);
      bounds.extend(position);
    });

    // Fit map to show all markers
    if (markersRef.current.length > 0) {
      map.fitBounds(bounds);
      // Prevent zooming in too much for single marker
      if (markersRef.current.length === 1) {
        google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          if (map.getZoom() > 15) {
            map.setZoom(15);
          }
        });
      }
    }
  }, [filteredGigs, mapLoaded, selectedDay]);

  // Parse YYYY-MM-DD string into local date parts, avoiding UTC timezone shift
  const formatDate = (dateString) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    let year, month, day;
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      [year, month, day] = dateString.split('-').map(Number);
      // Use local midnight to get correct day-of-week
      const date = new Date(year, month - 1, day);
      return {
        dayName: days[date.getDay()],
        day: date.getDate(),
        month: months[date.getMonth()],
        year: date.getFullYear()
      };
    }
    const date = new Date(dateString);
    return {
      dayName: days[date.getDay()],
      day: date.getDate(),
      month: months[date.getMonth()],
      year: date.getFullYear()
    };
  };

  // Returns a Google Maps URL using coordinates if available, otherwise address
  const getMapUrl = (gig) => {
    if (gig.lat && gig.lon) {
      return `https://www.google.com/maps?q=${gig.lat},${gig.lon}`;
    }
    const parts = [gig.venue_name, gig.address, gig.postcode, gig.city].filter(Boolean);
    if (parts.length > 0) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
    }
    return null;
  };

  return (
    <>
      <Helmet>
        <title>Find a YSR Performer - Your Song Request</title>
        <meta name="description" content="Find YSR performers gigging near you. Search by location and discover live music events." />
      </Helmet>
      
      <div className="min-h-screen bg-gray-900 text-gray-200">
        <Header />
        
        <main className="pt-24 pb-12">
          <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 flex items-center justify-center gap-3">
                <MapPin className="w-8 h-8 text-blue-400" />
                Find a YSR Performer
              </h1>
              <p className="text-lg text-gray-400 mb-2">
                Discover live music events and find YSR performers gigging near you
              </p>
              <p className="text-base text-gray-300">
                Artists and Performers all across the country are using Your Song Request to earn more money whilst gigging! Find one and enjoy the show!
              </p>
            </motion.div>

            {/* Main Content - Side by Side Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
              {/* Left Panel - Search and Filters */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-blue-900/30 backdrop-blur-sm rounded-xl p-6 border border-blue-700/50"
              >
                {/* Search Bar */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-white mb-2">
                    Enter a location or postcode
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300 w-5 h-5" />
                    <Input
                      type="text"
                      placeholder="e.g., London, SW1A 1AA"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-blue-800/50 border-blue-600 text-white placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* Days Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-white mb-2">
                    Filter Events on...
                  </label>
                  <Select value={selectedDay} onValueChange={setSelectedDay}>
                    <SelectTrigger className="w-full bg-blue-800/50 border-blue-600 text-white">
                      <SelectValue>
                        <div className="flex items-center space-x-2">
                          {selectedDay !== 'all' && (
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: dayColors[selectedDay] }}
                            />
                          )}
                          <span>
                            {selectedDay === 'all' ? 'All days' : selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}
                          </span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {['all', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <SelectItem 
                          key={day} 
                          value={day}
                          className="text-white hover:bg-gray-700 focus:bg-gray-700"
                        >
                          <div className="flex items-center space-x-2">
                            {day !== 'all' && (
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: dayColors[day] }}
                              />
                            )}
                            <span>
                              {day === 'all' ? 'All days' : day.charAt(0).toUpperCase() + day.slice(1)}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Frame Filter */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Filter Events within...
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: '28', label: '28 days' },
                      { value: '14', label: '14 days' },
                      { value: '7', label: '7 days' },
                      { value: '0', label: 'Today only' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                        <input
                          type="radio"
                          name="timeframe"
                          value={option.value}
                          checked={selectedTimeFrame === option.value}
                          onChange={() => setSelectedTimeFrame(option.value)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-white group-hover:text-blue-300 transition-colors">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Results Count */}
                {!loading && (
                  <div className="mt-6 pt-6 border-t border-blue-700/50">
                    <div className="text-sm text-blue-200">
                      Found <span className="font-bold text-white">{filteredGigs.length}</span> event{filteredGigs.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Right Panel - Map */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative rounded-xl overflow-hidden border border-gray-700"
                style={{ 
                  minHeight: mobile.isMobile ? '400px' : '600px', 
                  height: mobile.isMobile ? '400px' : 'calc(100vh - 200px)' 
                }}
              >
                {mapLoaded && window.google && window.google.maps ? (
                  <div ref={mapRef} className="w-full h-full" style={{ minHeight: 'inherit', height: 'inherit' }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-800" style={{ minHeight: 'inherit', height: 'inherit' }}>
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-gray-400">Loading map...</p>
                      {(!config.googleMapsApiKey || config.googleMapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY') && (
                        <p className="text-red-400 text-sm mt-2">Google Maps API key not configured</p>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Event List - Shown when filters are applied */}
            {!loading && filteredGigs.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-8"
              >
                <h2 className="text-2xl font-bold text-white mb-4">Events</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedGigs.map((gig, index) => {
                    const dateInfo = formatDate(gig.date);
                    return (
                      <Card key={gig.id} className="bg-gray-800/50 border-gray-700 hover:border-blue-500/50 transition-colors">
                        <CardContent className="p-4">
                          <h3 className="text-lg font-bold text-white mb-3">{gig.venue_name || gig.venue || 'Venue TBC'}</h3>
                          <div className="space-y-2 text-sm text-gray-300">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2 text-blue-400 flex-shrink-0" />
                              <span>
                                {dateInfo.dayName}, {dateInfo.day} {dateInfo.month} {dateInfo.year}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-green-400 flex-shrink-0" />
                              <span>{formatGigTime12h(gig.time)}</span>
                            </div>
                            <div className="flex items-start">
                              <MapPin className="w-4 h-4 mr-2 text-pink-400 flex-shrink-0 mt-0.5" />
                              <span>
                                {gig.address || 'Address TBA'}
                                {gig.postcode && ` • ${gig.postcode}`}
                              </span>
                            </div>
                            {gig.artist_name && (
                              <div className="flex items-center">
                                <Music className="w-4 h-4 mr-2 text-purple-400 flex-shrink-0" />
                                <Link to={`/${gig.artist_url_slug}`} className="text-blue-400 hover:text-blue-300 hover:underline font-medium">
                                  {gig.artist_name}
                                </Link>
                                {gig.artist_genre && (
                                  <span className="ml-2 text-gray-500">• {gig.artist_genre}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="mt-4 flex gap-2">
                            {getMapUrl(gig) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(getMapUrl(gig), '_blank')}
                                className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                              >
                                <Map className="w-3 h-3 mr-1" />
                                Map
                              </Button>
                            )}
                            {gig.artist_url_slug && (
                              <Link to={`/${gig.artist_url_slug}`} className="flex-1">
                                <Button
                                  size="sm"
                                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                >
                                  View Profile
                                </Button>
                              </Link>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className={
                              currentPage === pageNum
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "border-gray-600 text-gray-300 hover:bg-gray-700"
                            }
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Empty State */}
            {!loading && filteredGigs.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700 mt-8"
              >
                <MapPin className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No events found</h3>
                <p className="text-gray-400">
                  {searchTerm 
                    ? 'Try adjusting your search or filters' 
                    : 'No upcoming events match your filters'}
                </p>
              </motion.div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-black border-t border-gray-800 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2026 Your Song Request. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default FindPerformerPage;
